import { useState } from 'react';
import { usePropBotI18nContent } from './use-prop-bot-i18n';
import { PropBot } from '@/types/prop-bots';

// Helper function to detect original language from content
const detectOriginalLanguage = (content: string): string => {
  if (!content) return 'en';
  
  // Simple Vietnamese detection
  const vietnameseChars = /[àáạảãăắằặẳẵâấầậẩẫđéèẹẻẽêếềệểễíìịỉĩóòọỏõôốồộổỗơớờợởỡúùụủũưứừựửữýỳỵỷỹ]/gi;
  const vietnameseWords = /\b(và|của|có|không|được|với|từ|trong|cho|để|sẽ|về|tại|này|đó|theo|các|một|những|nhiều|khi|thì|nếu|như|hoặc|nhưng|vì|do|bởi|đã|đang|sẽ|phải|cần|nên|có thể|đối với)\b/gi;
  
  if (vietnameseChars.test(content) || vietnameseWords.test(content)) {
    return 'vi';
  }
  
  return 'en';
};

export const usePropBotMultilingualContent = (bot: PropBot | undefined, currentLanguage: string) => {
  const botId = bot?.id;
  const [shouldFetch, setShouldFetch] = useState(false);
  
  // Get original content from bot (prioritize propDetails description, fallback to bot description)
  const originalContent = bot?.propDetails?.[0];
  const originalDescription = originalContent?.description || bot?.description || '';
  
  // Detect original language from the description
  const originalLanguage = originalDescription 
    ? detectOriginalLanguage(originalDescription)
    : 'en';

  // Determine if we should show original or translated content
  const shouldShowOriginal = currentLanguage === originalLanguage;
  
  // OPTIMIZATION: Only fetch i18n when language doesn't match and user has interacted
  const { data: i18nContent, isLoading: isI18nLoading } = usePropBotI18nContent(
    botId, 
    currentLanguage,
    shouldFetch && !shouldShowOriginal
  );

  const hasTranslation = !!i18nContent;
  
  // Create merged content with proper fallback logic
  const multilingualContent = {
    description: shouldShowOriginal 
      ? originalDescription
      : i18nContent?.description || originalDescription,
    features: shouldShowOriginal
      ? originalContent?.features || []
      : i18nContent?.features || originalContent?.features || [],
    requirements: shouldShowOriginal
      ? originalContent?.requirements || []
      : i18nContent?.requirements || originalContent?.requirements || [],
    challenge_rules: shouldShowOriginal
      ? originalContent?.challenge_rules || []
      : i18nContent?.challenge_rules || originalContent?.challenge_rules || [],
  };

  // Check if we're showing translated content
  const isTranslated = !shouldShowOriginal && hasTranslation;
  const isOriginal = shouldShowOriginal;
  const isFallback = !shouldShowOriginal && !hasTranslation;

  return {
    multilingualContent,
    isLoading: isI18nLoading,
    isTranslated,
    isOriginal,
    isFallback,
    hasTranslation,
    originalLanguage,
    enableFetching: () => setShouldFetch(true)
  };
};